import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  IconButton,
  Dialog,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  CalendarMonth,
  ExpandMore,
  OpenInNew,
  LocationOn,
} from "@mui/icons-material";
import { DesktopDatePicker, DesktopTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { StylesConfig } from "react-select";
import placesApiKey from "../../components/utils/Google-Places-API-Key";
import { createJunkRemoval } from "../../redux/action/junk-removal";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";

const PLACES_API_KEY = placesApiKey;

interface AddressOption {
  label: string;
  value: any;
}

export interface JunkRemovalProps {
  onNext: (junkRemovalId?: string) => void;
  onBack: () => void;
}

const autocompleteStyles: StylesConfig<AddressOption, false> = {
  input: (provided) => ({
    ...provided,
    color: "#000000",
    height: "100%",
    borderRadius: "8px",
  }),
  option: (provided) => ({
    ...provided,
    color: "#808080",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#808080",
  }),
};

const JunkRemovalDetails: React.FC<JunkRemovalProps> = ({ onNext, onBack }) => {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [address, setAddress] = useState<AddressOption | null>(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [openDialog, setOpenDialog] = useState<{
    open: boolean;
    type: "date" | "time" | null;
  }>({
    open: false,
    type: null,
  });
  const { businessName } = useParams();
  useEffect(() => {
    const loadMaps = async () => {
      try {
        await new Promise((resolve) => {
          const script = document.createElement("script");
          script.src = `https://maps.googleapis.com/maps/api/js?key=${PLACES_API_KEY}&libraries=places`;
          script.async = true;
          script.defer = true;
          script.onload = resolve;
          document.head.appendChild(script);
        });
        setMapsLoaded(true);
      } catch (err) {
        console.error("Error loading Google Maps API:", err);
        setError("Failed to load maps service. Please refresh the page.");
      }
    };

    if (!mapsLoaded) {
      loadMaps();
    }
  }, [mapsLoaded]);

  const handleOpenDialog = (type: "date" | "time") => {
    setOpenDialog({ open: true, type });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (openDialog.type === "date") {
      setSelectedDate(event.target.value);
    } else if (openDialog.type === "time") {
      setSelectedTime(event.target.value);
    }
    setOpenDialog({ open: false, type: null });
  };

  const handleAddressChange = (value: AddressOption | null) => {
    setAddress(value);
  };

  const handleContinue = async () => {
    if (!address) {
      setError("Please enter an address");
      return;
    }
    if (!selectedDate || !selectedTime) {
      setError("Please select date and time");
      return;
    }
    const sendData = {
      removal_date: selectedDate,
      removal_time: selectedTime,
      removal_address: address.toString(),
    };
    try {
      const res = await dispatch<any>(createJunkRemoval(sendData));
      const result = unwrapResult(res);
      const newCanonicalId = result.canonical_id;
      onNext(newCanonicalId);
    } catch (err: any) {
      setError(err?.message || "Failed to create move service");
    }
    onNext();
  };

  const handleBack = () => {
    navigate(`/${businessName}/select-service`);
  };

  return (
    <Container
      sx={{
        width: "100%",
        height: isMobile ? "93vh" : "80vh",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
        padding: isMobile ? "20px" : "20px",
        background: "#f2f0f0",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            cursor: "pointer",
            py: 2,
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography fontSize={16} fontWeight={500}>
              Address for Junk Removal
            </Typography>
            {address?.label && (
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: "text.secondary" }}
              >
                {address.label}
              </Typography>
            )}
          </Box>
          <IconButton>
            <LocationOn color="primary" />
          </IconButton>
        </Box>

        {mapsLoaded && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <GooglePlacesAutocomplete
              apiOptions={{ language: "en" }}
              minLengthAutocomplete={1}
              apiKey={PLACES_API_KEY}
              debounce={1000}
              selectProps={{
                styles: autocompleteStyles,
                onChange: handleAddressChange,
                placeholder: "Enter Address",
                value: address,
              }}
            />
          </Box>
        )}

        <Divider sx={{ background: "#D3D3D3", mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            cursor: "pointer",
            py: 2,
          }}
          onClick={() => handleOpenDialog("date")}
        >
          <Typography fontSize={16} fontWeight={500}>
            Date of Junk Removal {selectedDate && `: ${selectedDate}`}
          </Typography>
          <IconButton>
            <CalendarMonth color="primary" />
          </IconButton>
        </Box>
        <Divider sx={{ background: "#D3D3D3", mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            cursor: "pointer",
            py: 2,
          }}
          onClick={() => handleOpenDialog("time")}
        >
          <Typography fontSize={16} fontWeight={500}>
            Time of Removal {selectedTime && `: ${selectedTime}`}
          </Typography>
          <IconButton>
            <ExpandMore color="primary" />
          </IconButton>
        </Box>
        <Divider sx={{ background: "#D3D3D3", mb: 2 }} />
      </Box>
      <Box display="flex" justifyContent="space-between" mt={3} gap={2}>
        <Button
          variant="outlined"
          sx={{
            borderRadius: "20px",
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            padding: isMobile ? "8px 20px" : "8px 45px",
            textTransform: "none",
            fontSize: isMobile ? "12px" : "14px",
            fontWeight: "600",
          }}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: "20px",
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            padding: isMobile ? "8px 20px" : "8px 45px",
            textTransform: "none",
            fontSize: isMobile ? "12px" : "14px",
            fontWeight: "600",
            "&:hover": { backgroundColor: "#357ABD" },
            "&:disabled": {
              backgroundColor: "#cccccc",
              color: "#666666",
            },
          }}
          onClick={handleContinue}
          disabled={!address || !selectedDate || !selectedTime || !mapsLoaded}
        >
          Continue
        </Button>
      </Box>
      <Dialog
        open={openDialog.open}
        onClose={() => setOpenDialog({ open: false, type: null })}
      >
        <Box p={3} width={300}>
          <Typography mb={2}>
            {openDialog.type === "date" ? "Select Date" : "Select Time"}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {openDialog.type === "date" ? (
              <DesktopDatePicker
                label="Select Date"
                value={selectedDate ? dayjs(selectedDate) : null}
                onChange={(date) =>
                  setSelectedDate(date?.format("YYYY-MM-DD") || "")
                }
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true,
                  },
                }}
              />
            ) : (
              <DesktopTimePicker
                label="Select Time"
                value={selectedTime ? dayjs(selectedTime, "HH:mm") : null}
                onChange={(time) =>
                  setSelectedTime(time?.format("HH:mm") || "")
                }
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true,
                  },
                }}
              />
            )}
          </LocalizationProvider>
        </Box>
      </Dialog>
    </Container>
  );
};

export default JunkRemovalDetails;
